import { useState, useEffect, useCallback } from 'react';
import {
  BasicSensorType,
  GatewaySensorType,
  SensorLatest,
  SubSensorType,
  VisibleSubSensor,
} from '../../services/api';

type AnySensor = SensorLatest | VisibleSubSensor;

interface UseSensorFiltersReturn {
  searchParam: string;
  selectionType: string;
  filteredSensors: AnySensor[] | undefined;
  availableSensorsType: (SubSensorType | GatewaySensorType | BasicSensorType)[] | undefined;
  setSearchParam: (value: string) => void;
  setSelectionType: (value: string) => void;
  setAvailableSensors: (sensors: AnySensor[]) => void;
  resetFilters: () => void;
}

export default function useSensorFilters(isAddingNew: boolean): UseSensorFiltersReturn {
  const [searchParam, setSearchParam] = useState('');
  const [selectionType, setSelectionType] = useState('all');
  const [availableSensors, setAvailableSensors] = useState<AnySensor[]>([]);
  const [filteredSensors, setFilteredSensors] = useState<AnySensor[] | undefined>();
  const [availableSensorsType, setAvailableSensorsType] = useState<
    (SubSensorType | GatewaySensorType | BasicSensorType)[] | undefined
  >();

  // Derive available sensor types whenever the base list changes
  useEffect(() => {
    const types = Array.from(new Set(availableSensors.map((s) => s.type).filter(Boolean))) as (
      | SubSensorType
      | GatewaySensorType
      | BasicSensorType
    )[];
    setAvailableSensorsType(types);
  }, [availableSensors]);

  // Re-filter whenever inputs change
  useEffect(() => {
    let result = availableSensors;

    if (selectionType !== 'all') {
      result = result.filter((s) => s.type === selectionType);
    }

    const regex = new RegExp(searchParam, 'i');

    const filteredResult = (sensors: SensorLatest[]) =>
      sensors.filter((s) => s?.id?.match(regex) || (s.name && s.name.match(regex)));

    result = isAddingNew
      ? (result as VisibleSubSensor[]).filter((s) => s?.suggested_id?.match(regex))
      : filteredResult(result as SensorLatest[]);

    setFilteredSensors(result);
  }, [availableSensors, searchParam, selectionType, isAddingNew]);

  const resetFilters = useCallback(() => {
    setSearchParam('');
    setSelectionType('all');
    setAvailableSensors([]);
    setFilteredSensors(undefined);
  }, []);

  return {
    searchParam,
    selectionType,
    filteredSensors,
    availableSensorsType,
    setSearchParam,
    setSelectionType,
    setAvailableSensors,
    resetFilters,
  };
}
